"use client";

import { useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";

type LeadMagnetFormProps = {
  source: string;
  buttonText?: string;
};

export function LeadMagnetForm({
  source,
  buttonText = "Pobierz darmowy raport",
}: LeadMagnetFormProps) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  async function submitForm(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await fetch("/api/lead-magnet/barometr-gpw-q1-2026", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, consent, source }),
      });
      const data = (await response.json()) as {
        error?: string;
        redirectUrl?: string;
      };

      if (!response.ok) {
        throw new Error(data.error ?? "Nie udało się wysłać formularza.");
      }

      window.location.assign(
        data.redirectUrl ?? "/barometr-gpw-q1-2026/dziekujemy",
      );
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Nie udało się wysłać formularza.",
      );
      setIsLoading(false);
    }
  }

  return (
    <form
      className="rounded-lg border border-[#3498db]/35 bg-[#101722]/95 p-5 shadow-[0_24px_90px_rgba(52,152,219,0.14)]"
      onSubmit={submitForm}
    >
      <div>
        <label className="text-sm font-semibold text-white" htmlFor={`${source}-email`}>
          E-mail
        </label>
        <input
          autoComplete="email"
          className="mt-2 min-h-12 w-full rounded-lg border border-white/10 bg-[#0e1117] px-4 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-[#3498db] focus:ring-2 focus:ring-[#3498db]/30"
          id={`${source}-email`}
          inputMode="email"
          name="email"
          onChange={(event) => setEmail(event.target.value)}
          placeholder="twoj@email.pl"
          required
          type="email"
          value={email}
        />
      </div>

      <label className="mt-4 flex gap-3 text-xs leading-5 text-slate-300">
        <input
          checked={consent}
          className="mt-1 size-4 shrink-0 rounded border-white/20 bg-[#0e1117] accent-[#3498db]"
          name="consent"
          onChange={(event) => setConsent(event.target.checked)}
          required
          type="checkbox"
        />
        <span>
          Chcę otrzymać raport „Barometr GPW Q1 2026” oraz zapisać się do
          newslettera RynkoweFakty z materiałami edukacyjnymi o giełdzie,
          gospodarce i wynikach spółek. Wiem, że mogę wypisać się w każdej
          chwili.
        </span>
      </label>

      <p className="mt-3 text-xs leading-5 text-slate-400">
        Administratorem danych jest RynkoweFakty. Szczegóły znajdziesz w{" "}
        <a className="font-semibold text-[#54afe9] hover:text-white" href="/polityka-prywatnosci">
          Polityce prywatności
        </a>{" "}
        oraz{" "}
        <a className="font-semibold text-[#54afe9] hover:text-white" href="/regulamin">
          Regulaminie
        </a>
        .
      </p>

      <button
        className="mt-5 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-[#3498db] px-5 py-3 text-sm font-semibold text-[#07111d] shadow-[0_0_30px_rgba(52,152,219,0.28)] transition hover:bg-[#54afe9] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3498db] disabled:cursor-wait disabled:opacity-80"
        disabled={isLoading}
        type="submit"
      >
        {isLoading ? (
          <>
            <Loader2 aria-hidden="true" className="size-4 animate-spin" />
            Wysyłam raport...
          </>
        ) : (
          <>
            {buttonText}
            <ArrowRight aria-hidden="true" className="size-4" />
          </>
        )}
      </button>

      {error ? (
        <p className="mt-3 text-xs leading-5 text-red-200">{error}</p>
      ) : null}
    </form>
  );
}
